import { MENU_GROUPS, MY_MENUS } from '../adminlayout/menuConfig';

// 한 메뉴가 열쇠 두 개를 받으면(permCode + permCodeAny, 출퇴근 관리 2026-09-29) 둘 다 그 자리 순서로 —
//   출퇴근 달력 메뉴가 빠져도 배정 화면의 '출퇴근 달력' 항목 순서가 전과 같다. permCodeAny 만 있는 메뉴는 전과 같이 순서 없음.
const STATIC_MENU_ORDER_CODES = [
  ...MY_MENUS,
  ...MENU_GROUPS.flatMap((group) => group.items || []),
].flatMap((item) => (item.permCode
  ? [item.permCode, ...(item.permCodeAny || []).filter((c) => c !== item.permCode)]
  : []));

const STATIC_MENU_ORDER = new Map(STATIC_MENU_ORDER_CODES.map((code, index) => [code, index]));

const DYNAMIC_COMPANY_MENU_ORDER_RULES = [
  { pattern: /^EXTERNAL_[A-Z0-9]+_USER_MANAGEMENT$/, order: 10 },
  { pattern: /^EXTERNAL_[A-Z0-9]+_ATTENDANCE_MANAGEMENT$/, order: 20 },
  { pattern: /^EXTERNAL_[A-Z0-9]+_LEAVE_MANAGEMENT$/, order: 30 },
  { pattern: /^EXTERNAL_[A-Z0-9]+_TM_CALL_MANAGEMENT$/, order: 40 },
  { pattern: /^EXTERNAL_[A-Z0-9]+_KEEP_MGMT$/, order: 41 }, // 킵 미팅종료 처리(2026-11-02) — 일일콜현황 관리 바로 아래
  { pattern: /^TM_SCORE_[A-Z0-9]+_MANAGEMENT$/, order: 45 },
  { pattern: /^MEETING_MAP_[A-Z0-9]+_MANAGEMENT$/, order: 46 }, // 회사별 미팅지도 관리(2026-10-22)
  { pattern: /^EXTERNAL_[A-Z0-9]+_CONTRACT_MANAGEMENT$/, order: 48 }, // 회사별 계약 관리(2026-10-15) — 계약취소 바로 앞
  { pattern: /^EXTERNAL_[A-Z0-9]+_PROMOTIONAL_MANAGEMENT$/, order: 49 }, // 회사별 홍보물 관리(2026-10-19)
  { pattern: /^CONTRACT_CANCEL_[A-Z0-9]+_MANAGEMENT$/, order: 50 },
  { pattern: /^REVENUE_[A-Z0-9]+_MANAGEMENT$/, order: 60 },
  { pattern: /^SETTLEMENT_[A-Z0-9]+_MANAGEMENT$/, order: 70 },
  { pattern: /^PAYOUT_[A-Z0-9]+_MANAGEMENT$/, order: 80 },
  { pattern: /^PAYROLL_[A-Z0-9]+_MANAGEMENT$/, order: 90 },
  { pattern: /^LEAVE_SETTLEMENT_[A-Z0-9]+_MANAGEMENT$/, order: 95 },
  { pattern: /^INCENTIVE_[A-Z0-9]+_MANAGEMENT$/, order: 100 },
  { pattern: /^EXTERNAL_[A-Z0-9]+_TERMINAL_MANAGEMENT$/, order: 110 },
];

const COMMON_PERMISSION_CODES = new Set(['EMPLOYEE_VIEW', 'WORK_ASSIGNMENT', 'INITIAL_SETUP_MANAGEMENT', 'DB_MANAGEMENT', 'LOG_MANAGEMENT']);
const FIXED_COMPANY_ORDER = new Map([['PINE', 0], ['DOT', 1], ['LNJ', 2]]);

function getDynamicCompanyMenuOrder(code) {
  for (const rule of DYNAMIC_COMPANY_MENU_ORDER_RULES) {
    if (rule.pattern.test(code)) return rule.order;
  }
  return null;
}

export function getMenuOrder(item, fallbackIndex) {
  const code = String(item.permCode || item.code || '').trim();
  if (STATIC_MENU_ORDER.has(code)) return STATIC_MENU_ORDER.get(code);

  const dynamicCompanyOrder = getDynamicCompanyMenuOrder(code);
  if (dynamicCompanyOrder !== null) return 1000 + dynamicCompanyOrder;

  if (/^INTEGRATED_MENU_\d+$/.test(code)) {
    const anchorOrder = STATIC_MENU_ORDER.get('EMPLOYEE_VIEW');
    if (anchorOrder !== undefined) return anchorOrder - 0.01;
  }

  const apiOrder = Number.isFinite(Number(item.sortIndex)) ? Number(item.sortIndex) : fallbackIndex;
  return 10000 + apiOrder;
}

// hqLabel: 회사 조각이 없는 코드(일반 메뉴)가 모이는 묶음의 머리글. 전엔 늘 'PINE' 글자였다 —
//   미팅링크·유기자차에서도 'PINE' 이 떠서, 보는 사람 테넌트의 대표 회사 이름을 넘겨받는다(2026-09-24). 없으면 'PINE'.
export function getItemCompanyGroup(item, hqLabel = null) {
  const code = String(item.permCode || item.code || '').trim();
  let companyCode = 'PINE';
  if (!code || COMMON_PERMISSION_CODES.has(code) || /^INTEGRATED_MENU_\d+$/.test(code)) companyCode = 'COMMON';
  else {
    // CONTRACT(2026-10-15): 회사별 계약 열쇠가 빠지면 본사 묶음에 잘못 들어간다
    const matched = code.match(/^EXTERNAL_([A-Z0-9]+)_(?:USER|ATTENDANCE|LEAVE|TM_CALL|TERMINAL|CONTRACT)_MANAGEMENT$/)
      || code.match(/^(?:CONTRACT_CANCEL|LEAVE_SETTLEMENT|TM_SCORE|REVENUE|SETTLEMENT|PAYOUT|PAYROLL|INCENTIVE)_([A-Z0-9]+)_MANAGEMENT$/)
      || code.match(/^MEETING_MAP_([A-Z0-9]+)_MANAGEMENT$/) // 회사별 미팅지도(2026-10-22) — 빠지면 본사 묶음에 들어간다
      || code.match(/^EXTERNAL_([A-Z0-9]+)_KEEP_MGMT$/) // 회사별 킵 미팅종료 처리(2026-11-02)
      || code.match(/^TM_FEATURE_EXT_([A-Z0-9]+)$/)
      || code.match(/^SETTLEMENT_([A-Z0-9]+)_FEATURE$/);
    if (matched) companyCode = matched[1];
  }

  if (companyCode === 'COMMON') return { key: 'company:COMMON', code: 'COMMON', label: '공통/시스템', order: 9000 };
  const labelMatch = String(item.label || '').match(/\(([^)]+)\)\s*$/);
  const label = companyCode === 'PINE' ? (hqLabel || 'PINE') : (labelMatch?.[1] || companyCode);
  const order = FIXED_COMPANY_ORDER.has(companyCode) ? FIXED_COMPANY_ORDER.get(companyCode) : 100;
  return { key: `company:${companyCode}`, code: companyCode, label, order };
}

export function groupItems(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.companyGroup.key;
    if (!groups.has(key)) groups.set(key, { ...item.companyGroup, items: [] });
    groups.get(key).items.push(item);
  }
  return Array.from(groups.values());
}
